import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { drawCircle, drawShape, isFirstPosition, isClickInsideShape, drawDroppedImage, redrawCanvas, attachDragListener } from '../../Utils/canvasUtils';
import './canvas.css';
import { clearDraggedItem, setCanvasRef, setShapePositions, setShapes } from '../../Reducers/mileSpaceSlice';
import { getSpaceData, updateSpace } from '../../Services/spaceServices';
import { useParams } from 'react-router-dom';
import { setIsDocumentChanged } from '../../Reducers/documentInfoSlice';
import WaterMark from '../WaterMark';
import { message } from 'antd';

const Canvas = ({ setFormValues, setDrawerVisible, contentLoading, setContentLoading }) => {
    const canvasRef = useRef(null);
    const dispatch = useDispatch();
    const { id } = useParams();
    const backgroundImage = useSelector((state) => state.mileSpace.spaceData.backgroundImage);
    const isDrawingMode = useSelector((state) => state.mileSpace.isDrawingMode);
    const shapes = useSelector((state) => state.mileSpace.spaceData.shapes);
    const shapePositions = useSelector((state) => state.mileSpace.spaceData.shapePositions);
    const currentDraggedItem = useSelector((state) => state.mileSpace.currentDraggedItem);
    const spaceData = useSelector((state) => state.mileSpace.spaceData);
    const userInfo = useSelector((state) => state.documentData.userInformation);
    const documentInfo = useSelector((state) => state.documentData.documentInformation);
    const isReadOnly = useSelector((state) => state.documentData.isReadOnly);
    
    // Fetch data from API and initialize state
    useEffect(() => {
        const fetchData = async () => {
            if (id) {
                setContentLoading(true);
                try {
                    await dispatch(getSpaceData(id)).unwrap();
                    if (canvasRef.current) {
                        dispatch(setCanvasRef(canvasRef));
                    }
                } catch (error) {
                    message.error("Unable to load content. The data is missing or unavailable.");
                } finally {
                    setContentLoading(false);
                }
            }
        };

        fetchData();
    }, [id, dispatch, setContentLoading]);


    const handleClick = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        if (!isDrawingMode) {
            // Check if clicked inside any shape's bounding box
            const clickedShape = shapes.find((shape) =>
                isClickInsideShape({ x, y }, shape.positions)
            );

            if (clickedShape) {
                setFormValues({
                    ...clickedShape,
                    name: clickedShape.name || '',
                    description: clickedShape.description || '',
                    length: clickedShape.length || '',
                    height: clickedShape.height || '',
                    birth: clickedShape.birth || '',
                });
                setDrawerVisible(true); // Open the drawer with shape details
            }
            return;
        }
        dispatch(setIsDocumentChanged(true));
        // Drawing mode logic
        const newPosition = { x, y };
        const updatedPositions = [...shapePositions, newPosition];
        dispatch(setShapePositions(updatedPositions));
        drawCircle(canvasRef, newPosition, shapePositions.length + 1, shapePositions.length === 0);
        if (shapePositions.length > 0) {
            drawShape(canvas.getContext('2d'), [...shapePositions, newPosition]); // Draw lines progressively
        }

        // Check if the new position is near the first position to complete the shape
        if (shapePositions.length > 0 && isFirstPosition(newPosition, shapePositions[0])) {
            //draw first circle back to red
            drawCircle(canvasRef, shapePositions[0], shapePositions.length + 1, false);
            const newShape = { id: uuidv4(), positions: [...shapePositions, newPosition] };
            const updatedShapes = [...shapes, newShape];
            dispatch(setShapes(updatedShapes));
            dispatch(setShapePositions([])); // Reset positions for next shape
            dispatch(updateSpace({
                id,
                updatedData: {
                    ...spaceData,
                    shapes: updatedShapes,
                    shapePositions: []
                }
            }));
        }
        else {
            dispatch(updateSpace({
                id,
                updatedData: {
                    ...spaceData,
                    shapePositions: updatedPositions
                }
            }));
        }
    };

    useEffect(() => {
        redrawCanvas(canvasRef, spaceData, dispatch);
    }, [backgroundImage]);

    // Cursor management for isDrawingMode
    useEffect(() => {
        const canvas = canvasRef.current;

        const handleMouseMove = (event) => {
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            if (!isDrawingMode) {
                // Check if mouse is over any shape or point
                const isOverShapeOrPoint = shapes?.some((shape) =>
                    isClickInsideShape({ x, y }, shape.positions)
                );

                canvas.style.cursor = isOverShapeOrPoint ? 'pointer' : 'default';
            } else {
                canvas.style.cursor = 'crosshair';
            }
        };

        canvas.addEventListener('mousemove', handleMouseMove);
        return () => {
            canvas.removeEventListener('mousemove', handleMouseMove);
        };
    }, [shapes, isDrawingMode]);

    const handleDrop = async (event) => {
        event.preventDefault();
        dispatch(setIsDocumentChanged(true));
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const draggedItem = currentDraggedItem;

        if (!draggedItem) return;
        const { title, originatingShapeId } = draggedItem;

        let updatedShapes = [...shapes];
        let validDrop = false;
        let isExistingShapeUpdated = false;

        // If the dragged item has an originating shape, we should update the shapes
        if (originatingShapeId) {
            updatedShapes = updatedShapes.map((shape) => {
                // Check if drop position is inside the shape
                if (isClickInsideShape({ x, y }, shape.positions)) {
                    if (shape.id !== originatingShapeId) {
                        validDrop = true;
                        // Add item to the new shape
                        const newImageData = { ...draggedItem, position: { x, y } };
                        drawDroppedImage(ctx, title, { x, y });
                        attachDragListener(canvas, draggedItem, shape.id, { x, y }, dispatch);
                        return {
                            ...shape,
                            images: [...(shape.images || []), newImageData],
                        };
                    }
                }
                if (shape.id === originatingShapeId) {
                    const newImageData = { ...draggedItem, position: { x, y } };
                    const existingImageIndex = shape.images?.findIndex((img) => img.id === draggedItem.id);
                    const isExistingShape = isClickInsideShape({ x, y }, shape.positions);

                    // Update the position of the existing image
                    if (existingImageIndex !== -1 && isExistingShape) {
                        const updatedImages = [...shape.images];
                        updatedImages[existingImageIndex] = { ...newImageData };
                        isExistingShapeUpdated = true;
                        attachDragListener(canvas, newImageData, shape.id, { x, y }, dispatch);
                        return {
                            ...shape,
                            images: updatedImages,
                        };
                    }
                }
                return shape;
            });
            if (validDrop) {
                updatedShapes = updatedShapes.map((shape) => {
                    if (shape.id === originatingShapeId) {
                        // Remove the item from the originating shape
                        return {
                            ...shape,
                            images: shape.images.filter((img) => img.id !== draggedItem.id),
                        };
                    }
                    return shape;
                });
            }
        } else {
            // If no originating shape, try adding the item to the shape that the drop happened inside
            updatedShapes = await Promise.all(
                shapes.map(async (shape) => {
                    if (isClickInsideShape({ x, y }, shape.positions)) {
                        const imageData = await drawDroppedImage(canvas.getContext('2d'), title, { x, y });
                        const newImageData = {
                            title: title,
                            ...imageData,
                            id: `item-${title}-${uuidv4()}`
                        };
                        attachDragListener(canvas, newImageData, shape.id, { x, y }, dispatch);
                        validDrop = true;
                        return {
                            ...shape,
                            images: [...(shape.images || []), newImageData]
                        };
                    }
                    return shape;
                })
            );
        }
        if (validDrop || isExistingShapeUpdated) {
            // Update Redux state and redraw the canvas
            dispatch(setShapes(updatedShapes));
            const updateSpaceData = {
                ...spaceData,
                shapes: updatedShapes
            };
            // Directly update canvas without clearing it
            // clearDroppedItem(shapes, draggedItem);

            // Redraw canvas with updated data
            redrawCanvas(canvasRef, updateSpaceData, dispatch);
            dispatch(updateSpace({ id, updatedData: updateSpaceData }));

        }
        isExistingShapeUpdated = false;
        validDrop = false;
        // Clear the dragged item once the drop process is complete
        dispatch(clearDraggedItem());
    };

    const clearDroppedItem = (previousShapes, draggedItem,) => {
        const { originatingShapeId } = draggedItem;
        // Clear the previous position of the dragged item
        if (originatingShapeId) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            const originatingShape = previousShapes.find((shape) => shape.id === originatingShapeId);
            if (originatingShape) {
                originatingShape.images.forEach((img) => {
                    if (img.id === draggedItem.id) {
                        // Use the width and height of the dragged item to clear the previous position
                        ctx.clearRect(img.position.x - 5, img.position.y - 5, draggedItem.width + 10, draggedItem.height + 10); // Adjust size as needed (5 for padding)
                    }
                });
            }
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    return (
        <div id='my-canvas'>
            <canvas
                className="canvas-container"
                ref={canvasRef}
                width={820}
                height={540}
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
            />
            {isReadOnly && userInfo.userId !== documentInfo.creator.id &&
                <WaterMark />
            }
        </div>
    );
};

export default Canvas;
